import { localized } from "@msuite/ui/localization";
import { ResponseError } from "@msuite/utils/errors";
import type { TranslationKey } from "@msuite/utils/locales";
import type { ClientResponse } from "hono/client";
import { toast } from "../toast";

/**
 * Handles the response from Hono RPC.
 * Automatically infers the "Success" type by stripping away the "Error" type (where success: false).
 * A coded error — `{ success: false, code, message, params? }`, the shape `errorMiddleware`
 * answers every refusal with — carries an i18n key as its message and is localised in the
 * client's own language, with `params` filled into the text, before it is toasted or thrown.
 * A 2xx whose body says `success: false` is the same failure: handlers that catch their database
 * error and answer 200 with a null result would otherwise be reported to the user as a success.
 *
 * A 401 is never toasted: it is a sign-in problem, and the session handling answers it.
 *
 * The `ResponseError` it throws carries the body's `code` and the **unlocalised** message key
 * beside the rendered message, so a caller that has to act on one particular refusal can identify
 * it without matching on translated text.
 */
export async function responseHandler<R extends ClientResponse<any, any, any>>(
	response: R,
	withToast = true,
): Promise<Exclude<Awaited<ReturnType<R["json"]>>, { success: false }>> {
	const payload = await response.json();
	if (!response.ok || payload?.success === false) {
		const message = payload?.code
			? localized(payload.message as TranslationKey, payload.params)
			: payload?.message ||
				payload?.error?.issues?.[0]?.message ||
				localized("general.request_failed");
		if (withToast && payload?.code !== "unauthorized" && typeof message === "string") {
			toast.error(message);
		}
		throw new ResponseError(message, response.status, {
			code: payload?.code ?? null,
			key: payload?.code ? (payload.message as string) : null,
		});
	}
	return payload as any;
}
